<template>
  <aside
    ref="root"
    class="action-header"
    :dir="['ar', 'ur'].includes(locale) ? 'rtl' : 'ltr'"
    @click.passive="trackCampaignClick"
  >
    <div class="campaign-inner">
      <div class="action-text" v-html="safeText" />
      <div v-if="hasProgress || timeUnits" class="campaign-details">
        <div
          v-if="hasProgress"
          class="campaign-progress"
          role="progressbar"
          :aria-label="t('actionHeader.progress')"
          :aria-valuenow="progress"
          aria-valuemin="0"
          aria-valuemax="100"
          :style="{ '--progress': progress + '%' }"
        >
          <span>{{ progress }}%</span>
        </div>
        <div
          v-if="timeUnits"
          class="countdown-container"
          role="timer"
          aria-live="off"
          :aria-label="t('actionHeader.remaining')"
        >
          <div
            v-for="unit in timeUnits"
            :key="unit.label"
            class="time-unit-block"
          >
            <span class="time-value">{{ unit.value }}</span
            ><span class="time-label">{{ unit.label }}</span>
          </div>
        </div>
      </div>
    </div>
  </aside>
</template>
<script setup>
import { ref, computed, onMounted, onBeforeUnmount, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import {
  campaignMarkup,
  campaignProgress,
  campaignCountdown,
  campaignStoreEvent
} from '~/utils/campaign-header';
const props = defineProps({
  progress: { type: Number, default: -1 },
  countdownTo: { type: Number, default: null },
  text: { type: String, required: true },
  showProgress: { type: Boolean, default: true }
});
const { track } = useConversionTracking();
function trackCampaignClick(event) {
  const anchor = event.target?.closest?.('a[href]');
  if (!anchor || !root.value?.contains(anchor)) return;
  const name = campaignStoreEvent(anchor.href);
  if (name) track(name);
}
const emit = defineEmits(['expired']);
const { t, locale } = useI18n();
const root = ref(null),
  now = ref(null);
const safeText = computed(() => campaignMarkup(props.text));
const progress = computed(() => campaignProgress(props.progress));
const hasProgress = computed(() => props.showProgress && progress.value > 0);
const timeUnits = computed(() => {
  const values = campaignCountdown(props.countdownTo, now.value);
  return values
    ? ['days', 'hours', 'minutes', 'seconds'].map((key, index) => ({
        value: values[index],
        label: t('actionHeader.' + key)
      }))
    : null;
});
let interval,
  observer,
  mounted = false,
  expiredDeadline;
function stop() {
  clearInterval(interval);
  interval = undefined;
}
function tick() {
  now.value = Date.now();
  if (
    Number.isFinite(props.countdownTo) &&
    props.countdownTo > 0 &&
    props.countdownTo <= now.value
  ) {
    stop();
    if (expiredDeadline !== props.countdownTo) {
      expiredDeadline = props.countdownTo;
      emit('expired', props.countdownTo);
    }
  }
}
function restart() {
  stop();
  if (!mounted) return;
  tick();
  if (
    !document.hidden &&
    Number.isFinite(props.countdownTo) &&
    props.countdownTo > now.value
  )
    interval = setInterval(tick, 1000);
}
function resize() {
  document.documentElement.style.setProperty(
    '--campaign-height',
    `${root.value?.getBoundingClientRect().height || 0}px`
  );
}
watch(() => props.countdownTo, restart);
onMounted(() => {
  mounted = true;
  restart();
  resize();
  if (typeof ResizeObserver !== 'undefined') {
    observer = new ResizeObserver(resize);
    observer.observe(root.value);
  } else window.addEventListener('resize', resize, { passive: true });
  document.addEventListener('visibilitychange', restart);
});
onBeforeUnmount(() => {
  mounted = false;
  stop();
  observer?.disconnect();
  window.removeEventListener('resize', resize);
  document.removeEventListener('visibilitychange', restart);
  document.documentElement.style.removeProperty('--campaign-height');
});
</script>
<style scoped>
.action-header {
  background: #241e19;
  color: #fff7ee;
  border-block-end: 1px solid #b99061;
  padding: 12px max(16px, env(safe-area-inset-left));
}
.campaign-inner {
  max-width: 1280px;
  margin-inline: auto;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-wrap: wrap;
  gap: 12px 24px;
  min-width: 0;
}
.action-text {
  flex: 1 1 320px;
  min-width: 0;
  text-align: center;
  font-size: clamp(0.9375rem, 0.85rem + 0.35vw, 1.125rem);
  font-weight: 600;
  line-height: 1.5;
  overflow-wrap: anywhere;
}
.action-text :deep(p) {
  margin: 0;
}
.action-text :deep(code) {
  color: #ffe0ac;
  background: #ffffff12;
  border: 1px solid #ffffff30;
  border-radius: 5px;
  padding: 2px 7px;
  font-weight: 800;
  unicode-bidi: isolate;
  direction: ltr;
}
.action-text :deep(a) {
  color: #ffe0ac;
  text-decoration: underline;
  text-underline-offset: 4px;
  text-decoration-thickness: 1px;
}
.action-text :deep(a:focus-visible) {
  outline: 2px solid currentColor;
  outline-offset: 4px;
  border-radius: 2px;
}
.campaign-details {
  display: flex;
  align-items: center;
  justify-content: center;
  gap: 12px;
  max-width: 100%;
}
.countdown-container {
  display: grid;
  grid-template-columns: repeat(4, minmax(0, 1fr));
  gap: 6px;
  width: min(100%, 288px);
}
.time-unit-block {
  min-width: 0;
  text-align: center;
  padding: 5px 6px;
  border: 1px solid #ffffff20;
  border-radius: 6px;
  background: #ffffff08;
}
.time-value {
  display: block;
  font-size: 1rem;
  font-weight: 700;
  font-variant-numeric: tabular-nums;
  line-height: 1.2;
}
.time-label {
  display: block;
  font-size: 0.6875rem;
  line-height: 1.4;
  color: #e9dccd;
  overflow-wrap: anywhere;
}
.campaign-progress {
  flex-shrink: 0;
  position: relative;
  isolation: isolate;
  overflow: hidden;
  min-width: 50px;
  border: 1px solid #b99061;
  border-radius: 6px;
  text-align: center;
  padding: 5px 8px;
  font-size: 0.8125rem;
  font-weight: 700;
}
.campaign-progress::before {
  position: absolute;
  z-index: -1;
  content: '';
  inset-block: 0;
  inset-inline-start: 0;
  width: var(--progress);
  background: #805830;
}
@media (max-width: 599px) {
  .action-text {
    flex-basis: 100%;
  }
  .campaign-inner {
    gap: 10px;
  }
  .campaign-details {
    width: 100%;
  }
  .countdown-container {
    flex: 1;
    max-width: 288px;
  }
}
@media (prefers-reduced-motion: reduce) {
  .action-header * {
    transition: none;
    animation: none;
  }
}
</style>
